(() => {
  const normalize = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const tokens = value => normalize(value).match(/[\p{L}\p{N}]+/gu) || [];
  let catalogPromise;
  const catalogUrl = () => document.querySelector('vk-tour-search')?.dataset.catalogUrl;
  const url = query => {
    const destination = new URL(catalogUrl(), location.origin);
    destination.search = '';
    destination.hash = query.trim() ? new URLSearchParams({ 'vk-query': query.trim() }).toString() : '';
    return destination;
  };
  async function fetchCatalog() {
    const products = new Map();
    const visited = new Set();
    let next = catalogUrl();
    let collectionId;
    if (!next) throw new Error('Tour collection unavailable');
    while (next) {
      const endpoint = new URL(next, new URL(catalogUrl(), location.origin));
      if (endpoint.origin !== location.origin) throw new Error('Unexpected catalog origin');
      endpoint.searchParams.set('section_id', 'vk-tour-catalog-data');
      if (visited.has(endpoint.href)) throw new Error('Repeated catalog page');
      visited.add(endpoint.href);
      const response = await fetch(endpoint, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Catalog unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = html.querySelector('[data-vk-tour-catalog]');
      if (!payload) throw new Error('Missing tour collection');
      const data = JSON.parse(payload.textContent);
      if (collectionId && collectionId !== data.collectionId) throw new Error('Unexpected collection');
      collectionId = data.collectionId;
      for (const tour of data.products) products.set(tour.id, tour);
      next = data.next;
    }
    return [...products.values()];
  }
  const load = () => {
    if (!catalogPromise) catalogPromise = fetchCatalog().catch(error => { catalogPromise = undefined; throw error; });
    return catalogPromise;
  };
  const search = (products, query) => {
    const words = tokens(query);
    if (!words.length) return query.trim() ? [] : products;
    return products.map(tour => {
      const title = normalize(tour.title);
      const text = normalize(`${tour.title} ${tour.text} ${tour.tags}`);
      return { tour, matches: words.every(word => text.includes(word)), score: words.reduce((score, word) => score + Number(title.includes(word)), 0) };
    }).filter(result => result.matches).sort((a, b) => b.score - a.score).map(result => result.tour);
  };
  const render = (container, template, tours, emptyText) => {
    const fragment = document.createDocumentFragment();
    for (const tour of tours) {
      const item = template.content.firstElementChild.cloneNode(true);
      item.querySelector('a').href = tour.url;
      item.querySelector('[data-vk-result-title]').textContent = tour.title;
      const image = item.querySelector('img');
      if (tour.image) image.src = tour.image;
      else image.remove();
      fragment.append(item);
    }
    if (!tours.length) {
      const item = document.createElement('li');
      item.className = 'vk-tour-search__empty';
      item.textContent = emptyText;
      fragment.append(item);
    }
    container.replaceChildren(fragment);
  };
  window.VkTourCatalog = { load, search, render, url };

  if (customElements.get('vk-tour-catalog-search')) return;
  customElements.define('vk-tour-catalog-search', class extends HTMLElement {
    connectedCallback() {
      const native = this.closest('.shopify-section')?.querySelector('[data-vk-catalog-native]');
      const input = this.querySelector('input[type="search"]');
      const list = this.querySelector('ul');
      const status = this.querySelector('[role="status"]');
      let revision = 0;
      const update = async () => {
        const current = ++revision;
        const query = new URLSearchParams(location.hash.slice(1)).get('vk-query')?.trim() || '';
        input.value = query;
        this.hidden = !query;
        if (native) native.hidden = Boolean(query);
        if (!query) { list.replaceChildren(); status.textContent = ''; return; }
        status.textContent = this.dataset.loading;
        list.replaceChildren();
        list.setAttribute('aria-busy', 'true');
        try {
          const results = search(await load(), query);
          if (current !== revision) return;
          render(list, this.querySelector('template'), results, this.dataset.empty);
          const plural = new Intl.PluralRules(document.documentElement.lang || 'en').select(results.length);
          const message = plural === 'one' ? this.dataset.countOne : this.dataset.countOther;
          status.textContent = message.replace('__count__', results.length);
        } catch {
          if (current === revision) status.textContent = this.dataset.error;
        } finally {
          if (current === revision) list.removeAttribute('aria-busy');
        }
      };
      const submit = event => {
        event.preventDefault();
        // This is our client-side fragment, not a Shopify collection filter parameter.
        history.pushState(null, '', url(input.value));
        update();
      };
      const clear = event => {
        event.preventDefault();
        history.pushState(null, '', url(''));
        update();
        const trigger = [...document.querySelectorAll('[data-vk-search-trigger]')].find(item => item.getClientRects().length);
        trigger?.focus({ preventScroll: true });
      };
      this.querySelector('form').addEventListener('submit', submit);
      this.querySelector('[data-vk-clear-query]').addEventListener('click', clear);
      window.addEventListener('hashchange', update);
      window.addEventListener('popstate', update);
      update();
      this.cleanup = () => {
        revision++;
        window.removeEventListener('hashchange', update);
        window.removeEventListener('popstate', update);
        this.querySelector('form').removeEventListener('submit', submit);
        this.querySelector('[data-vk-clear-query]').removeEventListener('click', clear);
      };
    }
    disconnectedCallback() { this.cleanup?.(); }
  });
})();
